import { useMemo } from "react";
import {
  PERMISSION_ACTIONS,
  PERMISSION_ACTION_LABEL,
  PERMISSION_GROUPS,
  type PermissionAction,
} from "@colab/shared";
import { cn } from "@/lib/utils";

// SPA port of /app/admin/settings/roles/_components/PermissionsField.tsx.
// Fully controlled — parent owns the Set<PermissionAction> state.
//
// Renders the full action list grouped by namespace (partners.* / deals.* /
// etc.). Disabled state cascades to all checkboxes so system roles render
// read-only without per-field plumbing.

type Props = {
  value: Set<PermissionAction>;
  onChange: (next: Set<PermissionAction>) => void;
  disabled?: boolean;
};

export function PermissionsField({ value, onChange, disabled = false }: Props) {
  const groups = useMemo(() => {
    const byKey: Record<string, PermissionAction[]> = {};
    for (const action of PERMISSION_ACTIONS) {
      const prefix = action.split(".")[0];
      (byKey[prefix] ??= []).push(action);
    }
    return PERMISSION_GROUPS.map((g) => ({
      ...g,
      actions: byKey[g.key] ?? [],
    })).filter((g) => g.actions.length > 0);
  }, []);

  function toggle(action: PermissionAction, checked: boolean) {
    const next = new Set(value);
    if (checked) next.add(action);
    else next.delete(action);
    onChange(next);
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        const checkedCount = g.actions.filter((a) => value.has(a)).length;
        const allChecked = checkedCount === g.actions.length;
        return (
          <fieldset
            key={g.key}
            className="rounded-md border bg-card/40 p-4"
            disabled={disabled}
          >
            <legend className="px-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {g.label}
              <span className="ml-2 text-muted-foreground/60">
                {checkedCount}/{g.actions.length}
              </span>
            </legend>
            <div className="mt-1 grid gap-x-4 gap-y-2 sm:grid-cols-2">
              {g.actions.map((action) => {
                const checked = value.has(action);
                return (
                  <label
                    key={action}
                    className={cn(
                      "flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                      checked && "bg-accent/[0.06] text-foreground",
                      !checked && "text-foreground/85 hover:bg-muted/50",
                      disabled && "cursor-not-allowed opacity-60",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => toggle(action, e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-accent"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block leading-tight">
                        {PERMISSION_ACTION_LABEL[action]}
                      </span>
                      <code className="mt-0.5 block text-[10px] text-muted-foreground">
                        {action}
                      </code>
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="sr-only" aria-live="polite">
              {allChecked
                ? "All selected"
                : `${checkedCount} of ${g.actions.length} selected`}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
